import { Directive, EmbeddedViewRef, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { FeatureFlagsService } from '../services/feature-flags.service';
import { Subject, takeUntil } from 'rxjs';
import { FeatureFlagKey } from '../types/feature-flag-key';

/**
 * Show content only is feature is enabled
 *
 * Example usage:
 *
 * ```
 * <div *ifFeatureFlagEnabled="'featureFlagKey'">
 *  content will be shown only when feature is _enabled_
 * </div>
 * ```
 */
@Directive({
  selector: '[ifFeatureFlagEnabled]',
  standalone: false,
})
export class FeatureFlagEnabledDirective implements OnInit, OnDestroy {
  /**
   * Feature flag key name
   */
  @Input() public ifFeatureFlagEnabled: FeatureFlagKey | undefined;

  private readonly _onDestroy$ = new Subject<void>();
  private _viewRef: EmbeddedViewRef<any> | undefined;

  constructor(
    private readonly template: TemplateRef<any>,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly featureFlagService: FeatureFlagsService,
  ) {}

  ngOnInit(): void {
    if (this.ifFeatureFlagEnabled === undefined) {
      return;
    }

    this.featureFlagService
      .isFeatureEnabled$(this.ifFeatureFlagEnabled)
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(isEnabled => {
        if (isEnabled) {
          if (this._viewRef != null) {
            // already created
            return;
          }

          this._viewRef = this.viewContainerRef.createEmbeddedView(this.template);
          this._viewRef.detectChanges();
        } else if (this._viewRef != null) {
          this._viewRef.destroy();
          this.viewContainerRef.clear();
          this._viewRef = undefined;
        }
      });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
    this._viewRef?.destroy();
  }
}
